import React from 'react';
import { motion } from 'framer-motion';
import { FlaskConicalIcon, MegaphoneIcon, MonitorIcon, UserRoundIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { PdfImage } from '../PdfImage';
import { boehringerImages } from '../../data/boehringerSnak';
const CANDIDATES = [{
  label: 'Webinar',
  detail: 'Turn one event into short evergreen chapters.',
  icon: VideoIcon,
  media: [{
    crop: boehringerImages.ctaScreen,
    alt: 'Snak menu inviting HCPs to Boehringer’s next online meeting'
  }]
}, {
  label: 'Website',
  detail: 'Add interaction to content already attracting visitors.',
  icon: MonitorIcon,
  media: [{
    crop: boehringerImages.kiapPage,
    alt: 'Boehringer page on chronic kidney disease in general practice'
  }, {
    crop: boehringerImages.loginPage,
    alt: 'Boehringer HCP portal login page'
  }]
}, {
  label: 'Campaign content',
  detail: 'Turn approved material into a short interactive campaign.',
  icon: MegaphoneIcon,
  media: [{
    crop: boehringerImages.emailFull,
    alt: 'Boehringer campaign email asking about high-risk T2D patients'
  }]
}, {
  label: 'Congress',
  detail: 'Create pre-, during- and post-event engagement.',
  icon: UsersIcon,
  media: [{
    crop: boehringerImages.quizScreen,
    alt: 'Interactive poll screen with three answer options'
  }]
}, {
  label: 'Clinical / dosing content',
  detail: 'Make complex information easier to explore.',
  icon: FlaskConicalIcon,
  media: [{
    crop: boehringerImages.statScreen,
    alt: 'Screen stating 332,507 people in Denmark have type 2 diabetes'
  }]
}, {
  label: 'Patient content',
  detail: 'Adapt approved HCP material into guided patient experiences.',
  icon: UserRoundIcon,
  media: [{
    crop: boehringerImages.coverPhoto,
    alt: 'Older patient and younger man looking at plants in a greenhouse'
  }]
}] as const;
export function SlideWhatToSnakify({}: SlideProps) {
  return <InteriorSlide banner={<SlideBanner data-id="element-366" data-mp="3sulscgx5w7fk">
          Snakification is not about the asset type. It is about making
          important information easier to notice, understand and act on.
        </SlideBanner>} data-id="element-365" data-mp="3stkkelbg0122">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex flex-shrink-0 flex-col gap-[clamp(0.5rem,1vw,0.9rem)] lg:flex-row lg:items-end lg:justify-between lg:gap-10" data-id="element-367" data-mp="pdvkdwi0rkb8">
        <div className="min-w-0" data-id="element-368" data-mp="354htsnytyxvf">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-369" data-mp="355jdfgtwwsor">
            The principle
          </p>
          <h2 className="max-w-3xl text-[clamp(1.7rem,3.2vw,2.9rem)] font-extrabold uppercase leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-370" data-mp="1fncqnq3mtd81">
            If it matters,{' '}
            <span className="text-[var(--brand)]" data-id="element-371" data-mp="1pl0o4nsmgytw">
              make it easier to engage with.
            </span>
          </h2>
        </div>
        <p className="max-w-sm flex-shrink-0 text-[clamp(0.82rem,1.1vw,1rem)] font-semibold leading-snug text-[var(--muted)] lg:text-right" data-id="element-372" data-mp="1ucscqss7wuhe">
          Anything important enough to earn HCP attention and understanding can
          be Snakified.
        </p>
      </motion.header>

      <p className="mt-[clamp(0.7rem,1.4vw,1.2rem)] flex-shrink-0 text-[clamp(0.58rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-373" data-mp="28b05eoi819mr">
        For example
      </p>

      <div className="mt-[clamp(0.5rem,0.9vw,0.8rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.5rem,1vw,0.95rem)] sm:grid-cols-2 lg:grid-cols-3" data-id="element-374" data-mp="dy0lij6t5t9o">
        {CANDIDATES.map((candidate, index) => <motion.article key={candidate.label} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16 + index * 0.07,
        duration: 0.45
      }} className="flex min-h-0 flex-col rounded-2xl border border-[var(--fg)]/10 bg-[var(--surface)] p-[clamp(0.75rem,1.3vw,1.25rem)]" data-id="element-375" data-mp="29d60fw66ktg1" data-mpi={index}>
            <div className="flex min-h-0 flex-1 items-center gap-[clamp(0.5rem,1vw,0.9rem)]" data-id="element-376" data-mp="2eaxuowuasrne">
              <span className="flex h-[clamp(2rem,2.6vw,2.6rem)] w-[clamp(2rem,2.6vw,2.6rem)] flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand)]" data-id="element-377" data-mp="1qktp4jxjuhtr">
                <candidate.icon className="h-[clamp(1rem,1.3vw,1.25rem)] w-[clamp(1rem,1.3vw,1.25rem)] text-white" aria-hidden="true" data-id="element-378" data-mp="1zh2pq1x9jmhh" />
              </span>
              <div className="flex min-h-0 flex-1 items-center justify-end gap-[clamp(0.3rem,0.6vw,0.6rem)] self-stretch py-1" data-id="element-379" data-mp="16xdx2s0gm33f">
                {candidate.media.map(media => <div key={media.alt} className={`flex h-full min-h-0 items-center justify-end ${candidate.media.length > 1 ? 'max-w-[42%]' : 'max-w-[72%]'}`} data-id="element-380" data-mp="sswsnl662ueu">
                    <PdfImage crop={media.crop} alt={media.alt} className={`min-h-0 w-auto rounded-md bg-white object-contain shadow-[0_6px_18px_rgba(23,32,51,0.14)] ring-1 ring-[var(--fg)]/10 ${candidate.media.length > 1 ? 'max-h-[70%]' : 'max-h-full'} max-w-full`} data-id="element-381" data-mp="nuc0m912jh1h" />
                  </div>)}
              </div>
            </div>

            <h3 className="mt-[clamp(0.55rem,1vw,0.9rem)] flex-shrink-0 text-[clamp(0.66rem,0.9vw,0.82rem)] font-extrabold uppercase tracking-[0.18em] text-[var(--brand)]" data-id="element-382" data-mp="3mc7l3e7atrfd">
              {candidate.label}
            </h3>
            <p className="mt-[clamp(0.25rem,0.5vw,0.45rem)] flex-shrink-0 text-[clamp(0.85rem,1.2vw,1.15rem)] font-semibold leading-snug tracking-[-0.02em] text-[var(--fg)]" data-id="element-383" data-mp="1xt3cuowmkgj7">
              {candidate.detail}
            </p>
          </motion.article>)}
      </div>
    </InteriorSlide>;
}